
import { Component, EventEmitter, Input, OnDestroy, Output } from '@angular/core';
import { Router } from '@angular/router';

import { of, Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import moment from 'moment-timezone';

import { EventsOverTimeApiModel } from '../../../model/statisticsReport/events.over.time.api.model';
import { DataPointModel } from '../../../../Charts/models/data-point.model';
import { LineChartOptionsModel } from '../../../../Charts/models/lineChart/line-chart-options.model';
import { LineChartModel } from '../../../../Charts/models/lineChart/line-chart.model';
import { LineSeriesModel } from '../../../../Charts/models/lineChart/line-series.model';
import { ButtonTypesEnum } from '../../../../Forms/enums/button-types.enum';
import { TabsStyleTypesEnum } from '../../../../Layout/enums/tabs-types.enum';
import { DistributionStatus } from '../../../enum/distribution-status.enum';
import { messageSeverityEnum } from '../../../../Misc/enums/message-severity.enum';
import { CurrentUserStateService } from '../../../../../Core/Users/services/current-user-state.service';
import { LocalizationService } from '../../../../../Core/Localization/services/localization.service';
import { RecipientReportDataModel } from '../RecipientReport/recipient.report.data.model';
import { LinksReportModel } from './links-report.model';
import { DistributionReportStatisticTypesEnum } from '../../../enum/distribution-report-statistic-types.enum';
import { StatisticsReportService } from '../../../services/statistics.report.service';
import { StatisticsResourceStatus } from '../../../enum/statistics-resource-status.enum';
import { StatisticReportSourceType } from '../../../model/statisticsReport/statistic-report-source-type.enum';

export enum EventsOverTimeTabs {
    first24Hours,
    first7Days
}

@Component({
    selector: 'agpr-performance-report',
    templateUrl: './performance.report.component.html',
    styleUrls: ['./performance.report.component.scss', '../report.placeholder.scss'],
    standalone: false
})
export class PerformanceReportComponent implements OnDestroy {
    private subscriptions: Subscription[] = [];

    readonly statisticsResourceStatus = StatisticsResourceStatus;
    readonly buttonTypes = ButtonTypesEnum;
    readonly tabsStyleTypes = TabsStyleTypesEnum;
    readonly distributionStatuses = DistributionStatus;
    readonly messageSeverities = messageSeverityEnum;
    readonly distributionStatTypes = DistributionReportStatisticTypesEnum;

    @Input() public set enableMppEstimates(value: boolean) {
        if (value !== this._enableMppEstimates) {
            this._enableMppEstimates = value;
        }
        this.statisticsReportService.setMppEstimates(this.enableMppEstimates);
    }
    @Input() set sentDate (sentDate: Date) {
        if (sentDate) {
            this._sentDate = sentDate;
            this.chartTabChange(0);
        }
    };

    @Input() status: StatisticsResourceStatus;
    @Input() clickTracking: boolean = true;
    @Input() openTracking: boolean = true;
    @Input() placeholderTitle: string = 'Discover.distributionReport.reportPlaceholder.sent';
    @Input() statisticReportSourceType = StatisticReportSourceType.DISTRIBUTION;
    @Input() dataNotAvailable: boolean = false;
    @Input() isRSSFeed: boolean = false;
    @Input() isScheduled: boolean = true;

    @Output() mppEstimateChanged = new EventEmitter<boolean>();
    @Output() selectedLinkChanged = new EventEmitter<string>();

    _sentDate: Date;
    recipientReportData: RecipientReportDataModel;
    eventsOverTime: EventsOverTimeApiModel;
    math: Math = Math;
    selectedTabIndex = 0;
    clickUnlessSelectors: string[] = ['a'];

    // TODO : use a DateAndValueXYChart ?;
    lineChart: LineChartModel;
    linksReport: LinksReportModel;

    ctrTooltipString;
    selectedLink: string;
    distributionStatus: DistributionStatus;

    userTimeZone: string;
    userPrefsMppEstimates: boolean = false;
    _enableMppEstimates: boolean;

    isDistribution: boolean = false;

    constructor(
        public localizationService: LocalizationService,
        public statisticsReportService: StatisticsReportService,
        public currentUserStateService: CurrentUserStateService,
        private router: Router,
    ) {
        this.isDistribution = this.router.url.includes('distributions');

        this.subscriptions.push(
            this.statisticsReportService.recipientReportData$.subscribe(recipientReportData => {
                this.statisticsReportService.setMppEstimates(this._enableMppEstimates);
                this.setRecipientReportData(recipientReportData);
            }),
            this.statisticsReportService.eventsOverTime$.pipe(filter(x => !!x)).subscribe(x => {
                this.eventsOverTime = x;
                this.buildChart();
            }),
            this.statisticsReportService.linksReport$.subscribe(linksReport => {
                this.linksReport = linksReport;
            }),
            this.currentUserStateService.getPreferences().pipe(filter(x => !!x)).subscribe(prefs => {
                this.userTimeZone = prefs?.timeZone;
            }),
        );
        this.ctrTooltipString =
        '<div style="font-weight:700;">' +
            this.localizationService.transform('Discover.distributionReport.performance.links.ctr.tooltip.bold') +
        '</div>' +
            this.localizationService.transform('Discover.distributionReport.performance.links.ctr.tooltip.normal');
    }

    ngOnDestroy() {
        this.subscriptions.forEach(
            subscription => subscription.unsubscribe()
        );
    }

    setRecipientReportData(recipientReportData: RecipientReportDataModel) {
        this.recipientReportData = recipientReportData;
    }

    chartTabChange(index: EventsOverTimeTabs) {
        if (!this._sentDate) {
            return;
        }
        if (index === EventsOverTimeTabs.first24Hours) {
            const query = {
                timeInterval : {
                    start: new Date(this._sentDate.getTime()),
                    end: new Date(this._sentDate.getTime() + (25 * 60 * 60 * 1000))
                },
                sliceSizeMs : 60 * 60 * 1000
            };
            this.statisticsReportService.setEventsOverTimeQuery(query);
        } else {
            const query = {
                timeInterval : {
                    start: new Date(this._sentDate.getTime()),
                    end: new Date(this._sentDate.getTime() + (8 * 24 * 60 * 60 * 1000)),
                },
                sliceSizeMs : 24 * 60 * 60 * 1000
            };
            this.statisticsReportService.setEventsOverTimeQuery(query);
        }
        this.selectedTabIndex = index;
    }

    buildChart() {
        if (!this.eventsOverTime) {
            return;
        }

        let maxY = 0;
        this.eventsOverTime.clicks?.forEach(x =>  {
            if (x.count > maxY) {
                maxY = x.count;
            }
        });
        this.eventsOverTime.opens?.forEach(x =>  {
            if (x.count > maxY) {
                maxY = x.count;
            }
        });
        maxY = Math.max(10, maxY * 1.1);

        const chartSeries: LineSeriesModel[] = [];
        const chartOptions: LineChartOptionsModel = {
            xValueLabel: this.localizationService.transform('Discover.distributionReport.performance.chartXLabel'),
            yValueLabel: this.localizationService.transform('Discover.distributionReport.performance.chartYLabel'),
            minYValue: 0,
            maxYValue: maxY,
            maxXValue: this.selectedTabIndex === EventsOverTimeTabs.first24Hours ? 24 : 7,
            precision: 0,
            strictMinMax: true,
            curved: true,
        };

        if ((!this.clickTracking && !this.openTracking)) {
            this.lineChart = null;
            return;
        }

        let xValue = 0;

        if (this.openTracking) {
            chartSeries.push(
                {
                    label: this.localizationService.transform('Discover.opens'),
                    dataProvider: of(this.eventsOverTime.opens.map(x => {
                        xValue++;
                        return <DataPointModel>{
                            label: '' + xValue,
                            xValue: xValue,
                            yValue: x.count
                        };
                    }))
                }
            );
            xValue = 0;
        }

        if (this.clickTracking) {
            chartSeries.push(
                {
                    label: this.localizationService.transform('Discover.clicks'),
                    dataProvider: of(this.eventsOverTime.clicks.map(x => {
                        xValue++;
                        return <DataPointModel>{
                            label: '' + xValue,
                            xValue: xValue,
                            yValue: x.count
                        };
                    }))
                }
            );
        }

        this.lineChart = new LineChartModel(chartSeries, chartOptions);
    }

    toXAxisValue(date: Date) {
        if (this.selectedTabIndex === EventsOverTimeTabs.first24Hours) {
            return moment(date).utc().hour();
        } else {
            return moment(date).utc().day() + 1;
        }
    }

    selectLink(link: string) {
        this.selectedLink = link;
        this.selectedLinkChanged.next(link);
    }

    openLink(link: string) {
        window.open(link);
    }

    changeMppEstimates() {
        this.statisticsReportService.setMppEstimates(this._enableMppEstimates);
        this.mppEstimateChanged.next(this._enableMppEstimates);
    }
}
